import { inject, injectable, interfaces } from 'inversify';
import { ACDLError } from '../../../models/error.model';
import { IMainApiService, IPagedResult } from '../../../services/api.service';
import { IApiService } from '../../../services/api.service.base';
import { IApiServiceExport } from '../../../services/api.service.export';
import { IProjectListDto } from '../projects.model';
import { ProjectOwnership } from '../projects.store';

interface IGetProjectsRequest {
  workspaceId: string;
  pageNumber: number;
  pageSize: number;
  orderBy: string;
  orderType: string;
  ownership: ProjectOwnership;
}

interface IGetProjectExclusiveDatasetsRequest {
  projectId: string;
}

interface IDeleteProjectRequest {
  projectId: string;
  deleteDatasets: boolean;
}

interface IImportProjectRequest {
  projectId: string;
  importAnnotations: boolean;
  importImageSets: boolean;
  workspaceId: string;
  file: File;
}

export interface IProjectsApiService {
  getProjectsAsync(request: IGetProjectsRequest): Promise<IPagedResult<IProjectListDto> | ACDLError>;
  getProjectExclusiveDatasetsAsync(request: IGetProjectExclusiveDatasetsRequest): Promise<string[] | ACDLError>;
  deleteProjectAsync(request: IDeleteProjectRequest): Promise<void | ACDLError>;
  importProjectAsync(request: IImportProjectRequest): Promise<void | ACDLError>;
}

export namespace IProjectsApiService {
  export const $: interfaces.ServiceIdentifier<IProjectsApiService> = Symbol('PROJECTS_API_SERVICE');
}

@injectable()
export class ProjectsApiService implements IProjectsApiService {
  constructor(
    @inject(IMainApiService.$) private readonly apiService: IApiService,
    @inject(IApiServiceExport.$) private readonly exportApiService: IApiServiceExport
  ) {}

  async getProjectsAsync(request: IGetProjectsRequest): Promise<IPagedResult<IProjectListDto> | ACDLError> {
    return await this.apiService.getAsync<IPagedResult<IProjectListDto>>('/Projects/All', { params: request });
  }

  async getProjectExclusiveDatasetsAsync(request: IGetProjectExclusiveDatasetsRequest): Promise<string[] | ACDLError> {
    return await this.apiService.getAsync<string[]>('/Projects/ExclusiveDatasets', { params: request });
  }

  async deleteProjectAsync(request: IDeleteProjectRequest): Promise<void | ACDLError> {
    return await this.apiService.postAsync('/Projects/delete', request);
  }

  async importProjectAsync(request: IImportProjectRequest): Promise<void | ACDLError> {
    const formData = new FormData();
    formData.append('projectDraftId', request.projectId);
    formData.append('importAnnotations', request.importAnnotations.toString());
    formData.append('importImageSets', request.importImageSets.toString());
    formData.append('workspaceId', request.workspaceId);
    formData.append('file', request.file);
    formData.append('fileName', request.file.name);

    return await this.exportApiService.postAsync('/Import/ImportProjectFromFile', formData);
  }
}
